import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledTypography } from '../styles/styles';
import ContentVersionComparisonView from './ContentVersionComparisonView';
import FullContentDiffViewer from './FullContentDiffViewer';
import ContentVersionModelInfoCard from './ContentVersionModelInfoCard';
import { useContentVersionData } from './useContentVersionData';

interface ContentVersionViewerProps {
  tone?: keyof typeof Palette;
}

const ContentVersionViewer: React.FC<ContentVersionViewerProps> = ({ tone = 'default' }) => {
  const { activityLogId } = useParams<{ activityLogId: string }>();
  const navigate = useNavigate();
  const {
    loading,
    loadingMessage,
    error,
    activityLog,
    currentContent,
    fullContent,
    previousContent,
    diff,
    currentUser,
  } = useContentVersionData(activityLogId);

  if (loading) {
    return (
      <StyledSection tone={tone}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
        {loadingMessage && <StyledTypography>{loadingMessage}</StyledTypography>}
      </StyledSection>
    );
  }

  if (error) {
    return (
      <StyledSection tone={tone}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <StyledButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Go Back
        </StyledButton>
      </StyledSection>
    );
  }

  if (!fullContent) {
    return (
      <StyledSection tone={tone}>
        <ContentVersionComparisonView
          tone={tone}
          activityLog={activityLog}
          diff={diff}
          previousContent={previousContent}
          currentContent={currentContent}
        />
      </StyledSection>
    );
  }

  return (
    <StyledSection tone={tone}>
      {activityLog && <ContentVersionModelInfoCard activityLog={activityLog} tone={tone} />}

      {activityLog && (
        <FullContentDiffViewer
          fullContent={fullContent}
          modelType={activityLog.associatedModelType}
          modelId={activityLog.associatedModelExternalId}
          currentUser={currentUser || undefined}
        />
      )}
    </StyledSection>
  );
};

export default ContentVersionViewer;
